import type { EventBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { Undo2, Zap } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { SegmentReuseNotice } from '../segment-picker/SegmentReuseNotice';
import { isTriggerStepUsage } from '../segment-picker/segment-usage';
import styles from '../EditAutomationTemplate.module.css';
import { TriggerExclusionSummary } from './TriggerExclusionSummary';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { SegmentTriggerConfigDialog } from './SegmentTriggerConfigDialog';

const { triggerStep, iconBadge, triggerUnsavedBadge, triggerUndoButton } = styles;

function getDescription(segmentName: string | null): string {
  return segmentName ? `When user enters: ${segmentName}` : 'Select a segment';
}

interface SegmentTriggerStepProps {
  triggerMetadata: EventBasedTriggerMetadata;
}

export const SegmentTriggerStep: React.FC<SegmentTriggerStepProps> = ({ triggerMetadata }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, undoTrigger } = editor;
  const [dialogIsOpen, setDialogOpenState] = useState(false);
  const { data: segmentDefinitions } = useSegmentDefinitions();

  const isDirty = state.dirtySteps.trigger;

  const selectedSegmentId = triggerMetadata.eventFilter?.segment_id ?? null;
  const selectedSegment = segmentDefinitions?.find(s => s.canonicalVersionId === selectedSegmentId);
  const segmentName = selectedSegment?.name || null;
  const description = getDescription(segmentName);

  return (
    <article className={triggerStep}>
      <header>
        <span className={iconBadge}>
          <Zap />
        </span>
        <h3>
          Trigger
          {isDirty && (
            <>
              <span className={triggerUnsavedBadge}>Unsaved changes</span>
              <button
                type="button"
                onClick={undoTrigger}
                className={triggerUndoButton}
                aria-label="Undo changes"
                title="Undo changes">
                <Undo2 />
              </button>
            </>
          )}
        </h3>
      </header>
      <p>{description}</p>
      <TriggerExclusionSummary />
      <SegmentReuseNotice isUsageOnThisStep={isTriggerStepUsage} />
      <button onClick={() => setDialogOpenState(true)} type="button">
        Configure →
      </button>
      {dialogIsOpen && (
        <SegmentTriggerConfigDialog
          onClose={() => setDialogOpenState(false)}
          triggerMetadata={triggerMetadata}
        />
      )}
    </article>
  );
};
